import { Brand } from '@awaze/design-system.components.awaze-theme-provider'

import { PlacesSearchConfigType } from 'src/components/SearchForm/SearchForm.types'
import { OPTIMIZELY_FLAGS_ENUM } from 'src/constants'
import { useAppConfig } from 'src/context/app-config-provider'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'

interface OptimizelyPlacesDataBrand {
  initialParentDestinationList?: Array<{
    locale: string
    placesApiId: string
    drupalJsonId: string
  }>
  placesApiAutosuggest?: Array<string>
  placesApiDestinationList?: Array<string>
  useLegacyDestinationSelector?: boolean
}

interface OptimizelyPlacesData {
  enableOnSearchMfe: boolean
  enableOnLandingMfe: boolean
  enableOnPropertyMfe: boolean
  pageSize: number
  pageSizePopular: number
  'cottages.com': OptimizelyPlacesDataBrand
  hoseasons: OptimizelyPlacesDataBrand
  novasol: OptimizelyPlacesDataBrand
  fincallorca: OptimizelyPlacesDataBrand
}

export const isOptimizelyPlacesData = (
  data: unknown,
): data is OptimizelyPlacesData => {
  return (
    data !== null &&
    typeof data === 'object' &&
    'enableOnSearchMfe' in data &&
    'enableOnLandingMfe' in data &&
    'enableOnPropertyMfe' in data &&
    'pageSize' in data &&
    'pageSizePopular' in data &&
    'cottages.com' in data &&
    'hoseasons' in data &&
    'novasol' in data &&
    'fincallorca' in data
  )
}

export const getBrandConfig = (
  data: OptimizelyPlacesData,
  brand: Brand,
): OptimizelyPlacesDataBrand | null => {
  if (
    brand === 'cottages.com' ||
    brand === 'hoseasons' ||
    brand === 'novasol' ||
    brand === 'fincallorca'
  ) {
    return data[brand]
  }

  return null
}

export const useUpdatedPlacesConfig = (
  brand: Brand,
  locale: string | undefined,
  placesSearchConfig: PlacesSearchConfigType,
): PlacesSearchConfigType => {
  const { features } = useAppConfig()
  const optimizelyVariableKey = features.placesApiOptimizelyVariableKey

  const optiContext = useOptimizelyDecisionsContext()
  const rawOptimizelyData = optiContext?.getExperimentVariables(
    OPTIMIZELY_FLAGS_ENUM.AGE_MFE_ACTIVATE_PLACES_API,
  )?.[optimizelyVariableKey]

  const optimizelyPlacesData = isOptimizelyPlacesData(rawOptimizelyData)
    ? rawOptimizelyData
    : undefined

  if (!optimizelyPlacesData) {
    // eslint-disable-next-line no-console
    console.error(
      `Optimizely Places Data is not in the correct format or is undefined for variable key: ${optimizelyVariableKey}`,
    )

    return placesSearchConfig
  }

  const brandConfig = getBrandConfig(optimizelyPlacesData, brand)

  // Is Places-XP- & Enable-On-Search-MFE -flag enabled in Optimizely
  const activatePlacesApiExperimentEnabled = optiContext.isExperimentEnabled(
    OPTIMIZELY_FLAGS_ENUM.AGE_MFE_ACTIVATE_PLACES_API,
  )
  const isEnabledOnSearchMfe = optimizelyPlacesData?.['enableOnSearchMfe']

  if (
    activatePlacesApiExperimentEnabled &&
    isEnabledOnSearchMfe &&
    brandConfig
  ) {
    const modifiedPlacesSearchConfig = { ...placesSearchConfig }

    modifiedPlacesSearchConfig.useLegacyDestinationSelector = Boolean(
      brandConfig.useLegacyDestinationSelector,
    )

    modifiedPlacesSearchConfig.initialParentDestinationList =
      brandConfig.initialParentDestinationList
        ?.filter((optimizelyObject) => optimizelyObject.locale === locale)
        .map((initParent) => initParent.placesApiId)
        .join() || undefined

    modifiedPlacesSearchConfig.pageSize = optimizelyPlacesData?.['pageSize']
    modifiedPlacesSearchConfig.pageSizePopular =
      optimizelyPlacesData?.['pageSizePopular']

    return modifiedPlacesSearchConfig
  }

  return placesSearchConfig
}
